// type
import type {
  AbbreviationLanguageType,
  AppPropsType,
} from '@/definition/common/data.d';
import type { AbTestFormDataType } from '@/pages/activityAbTest/utils/typing/create';
import type {
  CycleFormDataType,
  QpsTypeEnum,
} from '@/pages/activityCycle/utils/typing/create';
import type { FormDataType as SegmentDataType } from '@/pages/segmentsManage/utils/typings/addOrEdit';
import type { ReactNode } from 'react';
import type { FormValidateMessage } from 'tdesign-react';

/** 步骤 */
export enum StepEnum {
  /** 收件人 */
  TO = 'to',
  /** 发件人 */
  FROM = 'from',
  /** 测试设置 */
  TEST_SETUP = 'testSetup',
  /** 实验设置 */
  EXPERIMENT_SETTING = 'experimentSetting',
  /** 内容设置 */
  CONTENT = 'content',
  /** 递送计划 */
  SCHEDULE = 'schedule',
}

/** 语言、地点统计类型声明 */
export type LangCountryStatisticType = {
  name: string;
  value: number;
  percent: number;
};

/** 发送给 - 统计数据 */
export interface ToStatisticData {
  /** 订阅总数 */
  total: number;
  identity: string;
  /** 收件人 - 语言 */
  recipientLanguage: string[];
  /** 语言统计 */
  langList: LangCountryStatisticType[];
  /** 收件人 - 地点 */
  recipientCountry: string[];
  /** 区域统计 */
  regionList: LangCountryStatisticType[];
  /** 预计完成投递天数 */
  taskPredictDuration: number;
}

/** 步骤条 - 组件公用参数 */
export interface StepPropsType {
  // 初始化loading
  initLoading: boolean;
  /** 系统语言 */
  plugInLanguage: AbbreviationLanguageType;
  /** 是否体验角色 */
  isViewer?: boolean;
  /** 页面操作类型 */
  action?: string;
  /** 页面操作类型 */
  taskId?: number | string;
  /** 详情数据 - 是否请求完成 */
  reqCompleted?: boolean;
  /** 表单公用配置 */
  formCommonProps: {
    disabled: boolean;
  };
  /** 表单数据 */
  detailsData: any;
  /** 是否活动暂停 */
  isSuspended?: boolean;
  /** 是否内容模板弹窗的编辑 */
  isShowTempEdit?: boolean;
  /** 是否展示步骤操作按钮 */
  isStepOperation?: boolean;
  /** 需要完成的配置 */
  needCompleteConfig?: any[];
  /** 时区 */
  displayTimezone?: string;
  /** 是否ab测试 */
  isAbTest?: boolean;
  /** 是否周期活动 */
  isCycleActivity?: boolean;
  // 收件人统计
  toStatistics: ToStatisticData;
  /** 表单错误信息 */
  formErrorInfo?: FormValidateMessage<any>;
  setFormErrorInfo?: () => void;
  /** 选择内容 - 语言tab选中项 */
  languageActive?: string;
  setLanguageActive?: (val: string) => void;
  /** 发送到（To）统计loading */
  ruleDetailsLoading?: boolean;
  setRuleDetailsLoading?: (val: boolean) => void;
  /** 设置保存步骤 */
  setSaveList: (val: any) => void;
  /** 设置禁用步骤 */
  setDisabledList: (val: any) => void;
  /** 下一步 */
  onNext: (data: any) => void;
  onChange?: (data: any) => void;
  onValuesChange?: (data?: any) => void;
  /** 静默提交 */
  onSilentSubmit?: (data: any) => void;
  /** 跳转页面 */
  goToPages?: (url: string, params?: any) => void;
  /** 步骤按钮 - 右侧文本 */
  confirmText?: string;
  /** 步骤按钮 - 左侧渲染 */
  stepLeftOptRender?: ({ loading }: { loading?: boolean }) => ReactNode;
  dispatch?: AppPropsType['dispatch'];
}

/** 按钮 - 操作枚举 */
export enum OperationEnum {
  /** 默认提交 */
  DEFAULT_SUBMIT = 'DEFAULT_SUBMIT',
  /** 保存草稿 */
  SAVE_DRAFT = 'SAVE_DRAFT',
  /** 预览测试 */
  PREVIEW_TEST = 'PREVIEW_TEST',
}

/** to 规则类型 */
export enum ToTabsEnum {
  /** 默认 */
  default = 0,
  /** 标签 */
  label = 1,
  /** 分层 */
  Segment = 2,
  /** 所有联系人 */
  AllContact = 3,
  /** 内置分层 */
  builtinSegment = 4,
}

/** 邮件内容 - 语言类型 -  1 - 单语言 2 - 多语言 */
export enum LangTypeEnum {
  /** 单语言 */
  monolingual = 1,
  /** 多语言 */
  multilingual = 2,
}

/** 模板验证状态 */
export interface TempVerifyStatus {
  /** 占位符 - 未创建 */
  placeholder?: any[];
  /** 占位符 - 已创建 */
  staticPlaceholder?: string[];
  /** 字段 - 未设置默认值 */
  attributes?: any[];
  /** 模板是否含有占位符 */
  hasPlaceholder?: boolean;
  /** 是否开启取消订阅 */
  isUnsubscribed?: boolean;
  /** 是否设置订阅链接 */
  hasUnsubscribeOrPreferencesLink?: boolean;
  /** 模板是否被删除 */
  templateDeleted?: boolean;
  /** 未激活的表单 */
  unactiveForm?: any[];
  /** 模板内容是否包含测试链接 */
  hasTestLink?: boolean;
}

// 主题携带的未创建属性
export interface SubjectVerify {
  /** 主题携带的未创建属性 */
  subjectUncreatedAttrs?: string[];
}

/** 实验组 */
export interface ExperimentalItem extends SubjectVerify {
  subject: string;
  previewText: string;
  // 主题携带属性信息
  attributesDefaultValue?: Record<string, string>;
}

/** 实验组比例 */
export interface ExGroupProportionItem {
  proportion: number;
}

/** 是否手动插入 */
export enum MailContentItemIsExtraEnum {
  /** 是 */
  YES = 1,
  /** 否 */
  NO = 0,
}

/** 是否激活 */
export enum MailContentItemIsActiveEnum {
  /** 未激活 */
  UN_ACTIVATED = 0,
  /** 已活 */
  ACTIVATED = 1,
}

/** 邮件内容 - 多语言 */
export interface MailContentItem extends TempVerifyStatus, SubjectVerify {
  id?: number;
  lang: string;
  /** 邮件主题 */
  subject: string;
  /** 邮件预览文本（简介） */
  previewText?: string;
  /** 模板 */
  templateId: number;
  templateName: string;
  thumbnailUrl?: string;
  /** 初始变量 */
  variables?: string[];
  /** 当前语言设置的邮箱 */
  email?: string[];
  /** 是否手动新增 0 - 不是  1 - 是*/
  isExtra?: MailContentItemIsExtraEnum;
  /** 是否激活 0-未激活 1-已激活 */
  isActive?: MailContentItemIsActiveEnum;
  /** 是否填写完整 */
  status?: string;
  /** 有问题的变量 */
  errorVariables?: string[];
  /** 实验组数据 */
  experimental?: ExperimentalItem[];
  experimentalFlag?: boolean;
  /** 变量类型  1 - 固定 2 - 动态  */
  type?: 1 | 2;
  /** 联系人邮箱 */
  contactEmail?: string;
  /** hasPlaceholder  */
  hasPlaceholder?: boolean;
  /** hasUnsubscribeOrPreferencesLink  */
  hasUnsubscribeOrPreferencesLink?: boolean;
  /** isUnsubscribed  */
  isUnsubscribed?: boolean;
  // 默认值
  attributesDefaultValue?: Record<string, string>;
}

// 按地点配置
export interface DeliverTimeConfigItem {
  id?: number;
  /** 国家 */
  country: string;
  /** 开始投递时间（当地时区） */
  startTime: string;
  /** 开始投递时间（当地时区） */
  startTimeWithZone: string;
  /** 开始投递时间（UTC+8时区） */
  // startTimeUTC: string;
  /** 已投递数量 */
  subExecutionCount?: number;
  /** 任务数量 */
  subTaskCount?: number;
  /** 分组 */
  group?: number | string;
  /** 禁用 */
  disabled?: boolean;
  /** 表示修改过 */
  isModification?: boolean;
}

// 内置分层
export interface BuiltInSegmentItemType extends SegmentDataType {
  /** 唯一标识 */
  uuid: string;
  /** 名称 */
  name: string;
  value?: string;
  label?: string;
  group?: string;
}

export interface RegularFormDataType {
  /** 草稿保存步骤 */
  draftStage: string;
  id?: number;
  taskUuid?: string;
  /** 活动名称 */
  tag: string;
  /** 1：标签，2：细分规则 3: 全部联系人 */
  segmentType: number;
  /** 标签 */
  labelId: number;
  labelName: string;
  /** 分层 */
  filterGroupId: number;
  filterGroupName: string;
  /** 全部联系人 - 1.已订阅 2.全部 */
  subState: string;
  /** 发件人 */
  senderName: string;
  /** 发件地址前缀 */
  addressPrefix: string;
  /** 发件地址后缀 */
  domain: string;
  /** 发件地址后缀 */
  domainId: number;
  /** 邮件内容 - 1、单语言 2、多语言 */
  contentType: number;
  /** 邮件内容 - 默认语言 */
  defaultLangOptions: { text: string; value: any; proportion: number }[];
  /** 邮件内容 - 额外已选语言 */
  additionalLangSelected: any[];
  /** 邮件内容 - 默认语言 */
  defaultLang: string;
  /** 邮件内容 - 多语言 */
  emailContent: MailContentItem[];
  /** 递送类型 1：按固定时区 ，2：按用户所在时区；3：按地点配置 */
  deliverType: number;
  /** 时区 */
  timezone: string;
  /** 开始日期 */
  startDate?: string;
  /** 开始时间 */
  startTime?: string;
  /** 开始时间 */
  startTimeWithZone?: string;
  /** 开始日期是否禁用 */
  startDateDisabled?: boolean;
  /** 开始时间是否禁用 */
  startTimeDisabled?: boolean;
  /** 结束日期 */
  stopDate?: string;
  /** 结束时间 */
  stopTime?: string;
  /** 结束时间 */
  stopTimeWithZone?: string;
  /** 结束日期是否禁用 */
  stopDateDisabled?: boolean;
  /** 暂停时间 */
  suspendTime?: string;
  /** 按地点配置 */
  deliverTimeConfig: DeliverTimeConfigItem[];
  /** 分层计算频率类型 1 - 单次计算 2 - 周期调度 */
  qpsType?: QpsTypeEnum;
  /** 禁发时段设置 - 开关 */
  isForbiddenSend?: number;
  /** 禁发时段设置 - 开始时间 */
  forbiddenSendFrom?: string;
  /** 禁发时段设置 - 结束时间 */
  forbiddenSendTo?: string;
  /** 活动类型 */
  taskType?: number;
  taskDesc?: string;
  /** 是否跳过防打扰策略 */
  isSkipStrategy?: boolean;
  /** 开始时间修改后生效的周期数量 0-全部周期;N-周期数 */
  periodApplyNum?: number;
  /** 内置分层uuid */
  builtinSegmentUuid?: string;
  /** 发送给 */
  senderTo?: undefined | string;
  /** 内置分层 */
  builtinSegment?: BuiltInSegmentItemType[];
  /** 活动状态 */
  state?: number;
  // 取消订阅
  isEnableListUnsubscribe?: number;
  /** 模板内容是否包含测试链接 */
  hasTestLink?: boolean;
}
/** 表单 - type */
export interface FormDataType
  extends RegularFormDataType,
    CycleFormDataType,
    AbTestFormDataType {}

/** 递送策略 */
export enum DeliveryStrategyEnum {
  SCHEDULE = 0,
  SEND_NOW = 1,
}
/** 递送类型 */
export enum DeliverTypeEnum {
  /** 按固定时区 */
  fixed = 1,
  /** 按用户所在时区 */
  userLocation = 2,
  /** 按地点配置 */
  country = 3,
}

/** 提交表单 - 类型 */
export enum SubmitTypeEnum {
  /** 默认 */
  default = 'default',
  /** 草稿 */
  draft = 'draft',
  /** 跳转保存草稿 */
  jump_save_draft = 'jumpDraft',
}

// 基础类型定义
export type langItemType =
  | {
      id: number;
      lang: string;
      subject: string;
      templateId: number;
      templateName: string;
      experimental?: ExperimentalItem[];
      isDefault: number;
      variables: any[];
      isUnsubscribed: boolean;
      placeHolderHasValue: boolean;
      isPlaceHolderGreatThanValue: boolean;
      variablePair: Record<string, any>;
      email: string[];
      previewText: string;
      thumbnailUrl: string;
      errorVariables: any[];
      isExtra: number;
      templateDeleted: boolean;
      attributes: any | null;
      hasUnsubscribeOrPreferencesLink: boolean;
      hasPlaceholder: boolean;
      placeholder: any[];
      staticPlaceholder: any[];
      dynamicVariableList: any | null;
      isActive: number;
      templateForm: any | null;
      unactiveForm: any[];
      attributesDefaultValue: any | null;
      passPlaceholder: any | null;
      contactPlaceholder: any | null;
      status: string;
      [key: string]: any;
      label?: string;
    }
  | {
      [key: string]: unknown;
    };
